import { Link, data } from 'react-router';
import type { Route } from './+types/services.$slug';
import { buildMeta, breadcrumbSchema, serviceSchema, faqSchema } from '~/lib/seo';
import { SITE } from '~/data/site';
import { serviceBySlug, shippedServices } from '~/data/services';

export function loader({ params }: Route.LoaderArgs) {
  const service = serviceBySlug(params.slug);
  if (!service) throw data('Not found', { status: 404 });
  return { service };
}

export function meta({ data: loaded }: Route.MetaArgs) {
  if (!loaded) return [{ title: 'Not found' }];
  const { service } = loaded;
  return buildMeta({
    title: `${service.name} in Tampa Bay`,
    description: `${service.blurb} ${service.pricingNote}`,
    path: `/services/${service.slug}`,
    jsonLd: [
      breadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Services', url: '/services' },
        { name: service.name, url: `/services/${service.slug}` },
      ]),
      serviceSchema({
        name: service.name,
        description: service.blurb,
        path: `/services/${service.slug}`,
      }),
      faqSchema(service.faqs),
    ],
  });
}

export default function ServiceDetail({ loaderData }: Route.ComponentProps) {
  const { service } = loaderData;
  // shippedServices(), not SERVICES — otherwise the sidebar links to a cut page.
  const others = shippedServices().filter((s) => s.slug !== service.slug);
  const onRequest = service.tier === 'onRequest';

  return (
    <>
      <section className="hero dusk grain">
        <div className="shell">
          <div className="hero-inner">
            <p className="eyebrow-pill">
              {service.name}
              {onRequest && ' · on request'}
            </p>
            <h1>{service.headline}</h1>
            <p className="lede">{service.intro}</p>
            {onRequest && (
              <p className="small" style={{ color: '#c3d0da' }}>
                This one we quote job by job rather than running it on a standing schedule. If a
                specialist would do it better than us, we will say so — that is cheaper for you
                than finding out afterwards.
              </p>
            )}
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Get a quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The worry this service answers, said plainly. */}
      <section className="night-band">
        <div className="shell">
          <div className="stack" style={{ maxWidth: '44ch' }}>
            <p className="eyebrow">The actual worry</p>
            <h2 style={{ fontSize: 'var(--step-2)' }}>&ldquo;{service.worry}&rdquo;</h2>
            <p>
              That is the reason people call about this, so it is what the rest of this page
              answers.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="split">
            <div className="stack">
              <p className="eyebrow">What it covers</p>
              <h2>{onRequest ? 'What a quote covers.' : 'Every visit, on the checklist.'}</h2>
              <ul className="check">
                {service.includes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="stack split-sticky">
              <div className="callout">
                <p className="eyebrow">How it is priced</p>
                <p>{service.pricingNote}</p>
                <p>
                  <Link to="/pricing">See the full rate card →</Link>
                </p>
              </div>

              <div className="stack-sm">
                <h4>Other services</h4>
                <ul className="dash">
                  {others.map((s) => (
                    <li key={s.slug}>
                      <Link to={`/services/${s.slug}`}>{s.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rendered from service.faqs, the same array the FAQPage JSON-LD reads. */}
      <section className="section-tight">
        <div className="shell">
          <div className="faq">
            <h2>Questions about {service.name.toLowerCase()}</h2>
            {service.faqs.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="dawn">
        <div className="shell">
          <div className="cta-inner">
            <h2>Want a number for your building?</h2>
            <p>Twenty minutes on site and you get a fixed price in writing.</p>
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Book a walkthrough
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
